import React from "react";
import { Table, Button, Space, Dropdown, Typography } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import {
  MoreOutlined,
  ShareAltOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  FolderIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  CodeBracketIcon,
  CircleStackIcon,
  LockClosedIcon,
  StarIcon,
  HeartIcon,
  UserGroupIcon,
} from "@heroicons/react/24/solid";
import type { MenuProps } from "antd";
import { VaultFolder } from "@/types";
import { ShareBadge } from "@/components/shared";
import { formatRelativeDate } from "@/utils/formatters";

const { Text } = Typography;

const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  folder: FolderIcon,
  "folder-open": ArchiveBoxIcon,
  file: DocumentTextIcon,
  picture: PhotoIcon,
  video: VideoCameraIcon,
  sound: MusicalNoteIcon,
  code: CodeBracketIcon,
  database: CircleStackIcon,
  lock: LockClosedIcon,
  star: StarIcon,
  heart: HeartIcon,
  team: UserGroupIcon,
};

interface FolderTableProps {
  folders: VaultFolder[];
  isLoading?: boolean;
  selectedIds: Set<string>;
  onFolderClick: (folder: VaultFolder, e: React.MouseEvent) => void;
  onFolderDoubleClick: (folder: VaultFolder) => void;
  onFolderShare: (folder: VaultFolder) => void;
  onFolderRename: (folder: VaultFolder) => void;
  onFolderDelete: (folder: VaultFolder) => void;
  onSelectionChange: (selectedIds: string[]) => void;
  canShare?: boolean;
}

export default function FolderTable({
  folders,
  isLoading = false,
  selectedIds,
  onFolderClick,
  onFolderDoubleClick,
  onFolderShare,
  onFolderRename,
  onFolderDelete,
  onSelectionChange,
  canShare = true,
}: FolderTableProps) {
  const getMenuItems = (folder: VaultFolder): MenuProps["items"] => [
    ...(canShare
      ? [
          {
            key: "share",
            icon: <ShareAltOutlined />,
            label: "Share",
            onClick: () => onFolderShare(folder),
          },
          { type: "divider" as const },
        ]
      : []),
    {
      key: "rename",
      icon: <EditOutlined />,
      label: "Rename",
      onClick: () => onFolderRename(folder),
    },
    { type: "divider" as const },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      danger: true,
      onClick: () => onFolderDelete(folder),
    },
  ];

  const columns: ColumnsType<VaultFolder> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => {
        const IconComponent = iconMap[record.icon] || FolderIcon;
        const isShared = record.sharedWith?.length > 0;
        return (
          <Space>
            <IconComponent
              style={{
                width: 20,
                height: 20,
                color: record.color || "#0F6E56",
              }}
            />
            <Text ellipsis style={{ maxWidth: 300 }} title={name}>
              {name}
            </Text>
            {isShared && <ShareBadge />}
          </Space>
        );
      },
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 80,
      sorter: (a, b) => (a.itemCount ?? 0) - (b.itemCount ?? 0),
      render: (count) => (
        <Text type="secondary">{count ?? 0}</Text>
      ),
    },
    {
      title: "Modified",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 120,
      sorter: (a, b) =>
        new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime(),
      render: (date) => (
        <Text type="secondary">{date ? formatRelativeDate(date) : "-"}</Text>
      ),
    },
    {
      title: "Created by",
      dataIndex: ["createdBy", "name"],
      key: "createdBy",
      width: 140,
      render: (name) => (
        <Text type="secondary" ellipsis style={{ maxWidth: 120 }}>
          {name || "-"}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, record) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const rowSelection: TableProps<VaultFolder>["rowSelection"] = {
    selectedRowKeys: Array.from(selectedIds),
    onChange: (selectedRowKeys) => {
      onSelectionChange(selectedRowKeys as string[]);
    },
    columnWidth: 48,
  };

  if (folders.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div className="section-label">FOLDERS</div>
      <Table
        columns={columns}
        dataSource={folders}
        rowKey="id"
        rowSelection={rowSelection}
        loading={isLoading}
        pagination={false}
        size="middle"
        onRow={(record) => ({
          onClick: (e) => onFolderClick(record, e),
          onDoubleClick: () => onFolderDoubleClick(record),
          style: {
            cursor: "pointer",
            backgroundColor: selectedIds.has(record.id)
              ? "rgba(15, 110, 86, 0.04)"
              : undefined,
          },
        })}
      />
    </div>
  );
}
